import * as React from 'react';
import {useSelector} from 'react-redux';
import {Link} from 'react-router-dom';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';

import {Session} from 'nutshell-core/session';
import type {User} from 'nutshell-core/types';

import type {NutshellState} from '../../../store';

import {shouldClientRoute} from './helpers';

type SidebarNavLinkProps = {
    children: React.ReactNode;
    isActive: boolean;
    intercomKey?: string;
    onBasePath: boolean;
    tooltipText?: string;
    href: string;
    to?: string;
    variant?: 'default' | 'ai';
};

SidebarNavLink.defaultProps = {
    as: 'a',
};

function SidebarNavLinkComponent(props: SidebarNavLinkProps) {
    const loggedInUser: User | null | undefined = useSelector((state: NutshellState) =>
        Session.getSessionStateUser(state)
    );
    const {href, to, isActive, onBasePath, tooltipText, children, variant = 'default'} = props;

    const classNames = classnames('nut-sidebar--tab', {
        'nut-sidebar--tab--active': isActive && variant === 'default',
        'nut-sidebar--tab--collapsed': loggedInUser && loggedInUser.isMainSidebarCollapsed,
        'nut-sidebar--tab--ai': variant === 'ai' && !isActive,
        'nut-sidebar--tab--ai--active': variant === 'ai' && isActive,
    });

    React.useEffect(() => {
        if (onBasePath) {
            ReactTooltip.hide();
        }
    }, [onBasePath]);

    if (shouldClientRoute(to, window) && typeof to === 'string') {
        return (
            // @ts-expect-error
            <Link
                className={classNames}
                to={to}
                id={props.intercomKey ? `for-intercom-${props.intercomKey}` : undefined}
                data-tip={tooltipText}
                data-place='right'
                data-effect='solid'
                data-delay-show={0}
            >
                {children}
            </Link>
        );
    }

    return (
        <a
            className={classNames}
            href={href}
            data-tip={tooltipText}
            data-place='right'
            data-effect='solid'
            data-delay-show={0}
            id={props.intercomKey ? `for-intercom-${props.intercomKey}` : undefined}
        >
            {children}
        </a>
    );
}

type ErrorBoundaryProps = {
    linkProps: SidebarNavLinkProps;
    children: React.ReactNode;
};

function ErrorBoundary(props: ErrorBoundaryProps) {
    const loggedInUser = useSelector((state: NutshellState) => Session.getSessionStateUser(state));
    const [renderStandardLink, setRenderStandardLink] = React.useState(false);

    React.useEffect(() => {
        // @ts-expect-error
        const handleError = (e) => {
            if (
                e &&
                e.message === 'ResizeObserver loop completed with undelivered notifications.'
            ) {
                // eslint-disable-next-line no-console
                console.warn('ResizeObserver loop completed with undelivered notifications.');
            } else {
                setRenderStandardLink(true);
            }

            // @ts-expect-error
            if (typeof window.trackJs !== 'undefined') {
                // @ts-expect-error
                window.trackJs.track('sidebarNavLink Error');
            }
        };

        window.addEventListener('error', handleError);

        return () => window.removeEventListener('error', handleError);
    }, []);

    if (renderStandardLink) {
        // You can render any custom fallback UI
        const {to, isActive, tooltipText, children, href, variant = 'default'} = props.linkProps;
        const classNames = classnames('nut-sidebar--tab', {
            'nut-sidebar--tab--active': isActive && variant === 'default',
            // @ts-expect-error
            'nut-sidebar--tab--collapsed': loggedInUser.isMainSidebarCollapsed,
            'nut-sidebar--tab--ai': variant === 'ai' && !isActive,
            'nut-sidebar--tab--ai--active': variant === 'ai' && isActive,
        });

        return (
            <a
                className={classNames}
                href={to || href}
                data-tip={tooltipText}
                data-place='right'
                data-effect='solid'
                data-delay-show={0}
            >
                {children}
            </a>
        );
    }

    return props.children;
}

export function SidebarNavLink(props: SidebarNavLinkProps) {
    return (
        // @ts-expect-error
        <ErrorBoundary linkProps={props}>
            <SidebarNavLinkComponent {...props} />
        </ErrorBoundary>
    );
}
